'use client';

import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { DoorOpen, MessageCircle, Youtube } from 'lucide-react';
import { db } from '@/firebase/config';
import { cn, sanitizeFirestoreData } from '@/lib/utils';
import { detallesDelDia } from '@/lib/programa-ss';
import { ProcesionMap, ESCUDO_RING } from '@/components/cecop/ProcesionMap';
import { construirMapaDelDia, useCecopDiaActivo } from '@/components/cecop/cecop-dia';
import { youtubeId } from '@/components/cecop/ProcesionPublicCard';
import type {
  CecopDia, CecopDiaLive, DiaSS, ItinerarioPunto, JartinetHermandad,
} from '@/lib/types';

const btnShape = {
  clipPath: 'polygon(0% 0%, calc(100% - 16px) 0%, 100% 16px, 100% 100%, 16px 100%, 0% calc(100% - 16px))',
};

/** Fecha larga, tolerante a Timestamp o ISO (mismo criterio que el libro). */
function formatFecha(fecha: any): string {
  if (!fecha) return '';
  const d = typeof fecha?.toDate === 'function' ? fecha.toDate() : new Date(fecha);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
}

/**
 * Panel público de un DÍA de Semana Santa en directo: título del día, mapa con
 * el GPS de todas las cofradías que salen, la lista de esas cofradías y, si lo
 * hay, el directo de YouTube.
 *
 * Lo enciende el admin con «ACTIVAR» en el CECOP de Semana Santa. Sin sesión:
 * todo lo que lee es de lectura pública.
 */
export function useCecopDiaPublico() {
  const { cecop, live, dia } = useCecopDiaActivo();
  const [hermandades, setHermandades] = useState<JartinetHermandad[]>([]);

  // Los escudos y colores salen de `jartinet_hermandades` (lectura pública),
  // igual que en la sección del programa de la portada.
  useEffect(() => {
    if (!cecop) return;
    let cancelado = false;
    getDocs(collection(db, 'jartinet_hermandades'))
      .then((snap) => {
        if (cancelado) return;
        setHermandades(snap.docs.map((d) => sanitizeFirestoreData({ id: d.id, ...d.data() })) as JartinetHermandad[]);
      })
      .catch(() => { /* sin escudos el panel sigue funcionando */ });
    return () => { cancelado = true; };
  }, [cecop?.id]);

  return { cecop, live, dia, hermandades };
}

/** Etiqueta de cada tipo de punto del cuadro de horarios (`ItinerarioPunto.tipo`). */
const TIPO_ESTILO: Record<ItinerarioPunto['tipo'], string> = {
  salida: 'text-emerald-300',
  carreraOficial: 'text-amber-300',
  catedral: 'text-amber-200',
  entrada: 'text-rose-300',
  itinerario: 'text-white/80',
};

/**
 * Cuadro de horarios de UNA cofradía, tal y como lo guarda el programa:
 * `itinerario` es un `Record<'HH:MM', { calle, tipo }>`, así que se ordena por
 * la hora (la clave) y se pinta hora + calle.
 */
function CuadroHorariosPanel({
  nombre, color, escudoUrl, itinerario, onCerrar,
}: {
  nombre: string;
  color: string;
  escudoUrl?: string;
  itinerario?: Record<string, ItinerarioPunto>;
  onCerrar: () => void;
}) {
  const filas = Object.entries(itinerario || {}).sort(([a], [b]) => a.localeCompare(b));
  return (
    <div className="rounded-2xl border bg-black/35 p-3" style={{ borderColor: `${color}55` }}>
      <div className="mb-2 flex items-center gap-2">
        <span
          className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full border-2"
          style={{ background: '#0a0a0c', borderColor: color }}
        >
          {escudoUrl
            ? <img src={escudoUrl} alt="" className="h-5 w-5 object-contain" />
            : <span className="text-[0.6rem] font-black text-white/60">✚</span>}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-bold text-white">{nombre}</span>
          <span className="block text-[0.55rem] font-black uppercase tracking-[0.18em] text-white/45">
            Cuadro de horarios
          </span>
        </span>
        <button
          type="button"
          onClick={onCerrar}
          title="Volver a la lista de cofradías"
          className="shrink-0 rounded-full border border-white/15 px-2 py-0.5 text-[0.6rem] font-bold text-white/60 transition-colors hover:bg-white/10 hover:text-white"
        >
          ✕
        </button>
      </div>

      {filas.length === 0 ? (
        <p className="py-6 text-center text-xs text-white/40">Esta cofradía no tiene cuadro de horarios.</p>
      ) : (
        <div className="flex max-h-[420px] flex-col gap-0.5 overflow-y-auto pr-1">
          {filas.map(([hora, p]) => (
            <div key={hora} className="flex items-baseline gap-2 rounded-lg px-1.5 py-1 odd:bg-white/[0.03]">
              <span className="w-10 shrink-0 font-mono text-[0.65rem] font-bold" style={{ color }}>{hora}</span>
              <span className={cn('min-w-0 flex-1 text-[0.68rem] leading-tight', TIPO_ESTILO[p.tipo] || 'text-white/80')}>
                {p.calle}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CecopDiaPublicCard({
  cecop, live, dia, hermandades,
}: {
  cecop: CecopDia;
  live: CecopDiaLive | null;
  dia: DiaSS | null;
  hermandades: JartinetHermandad[];
}) {
  const vid = youtubeId(cecop.youtubeUrl);
  // Cofradía apuntada con el ratón en el mapa: mientras haya una, el panel de la
  // derecha enseña SU cuadro de horarios en vez de la lista "Hoy salen".
  // Al salir del marcador NO se limpia a propósito: si se limpiara, el cuadro
  // desaparecería justo al ir a leerlo.
  const [apuntada, setApuntada] = useState<string | null>(null);
  const { rutas, entries } = useMemo(
    () => construirMapaDelDia({ cecop, dia, hermandades, live, seleccion: apuntada }),
    [cecop, dia, hermandades, live, apuntada],
  );
  const detalles = useMemo(() => detallesDelDia(dia), [dia]);
  const fecha = formatFecha(cecop.fecha);

  const filaApuntada = (cecop.hermandades || []).find((h) => h.hermandadId === apuntada) || null;
  const detalleApuntado = detalles.find((d) => d.hermandadId === apuntada) || null;
  const hermandadApuntada = hermandades.find((h) => h.id === apuntada) || null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className="relative mx-auto w-full max-w-6xl overflow-hidden rounded-3xl border p-5 backdrop-blur-md md:p-7"
      style={{
        borderColor: `${ESCUDO_RING}55`,
        background: 'rgba(8,8,10,.72)',
        boxShadow: `0 0 40px ${ESCUDO_RING}22, 0 20px 60px rgba(0,0,0,.6)`,
      }}
    >
      <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full blur-3xl" style={{ background: `${ESCUDO_RING}33` }} />

      <div className="relative flex flex-col items-center gap-3">
        <motion.span
          animate={{ scale: [1, 1.06, 1], boxShadow: ['0 0 0 0 rgba(244,63,94,.5)', '0 0 0 12px rgba(244,63,94,0)', '0 0 0 0 rgba(244,63,94,0)'] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          className="inline-flex items-center gap-2 rounded-full border border-rose-500/50 bg-rose-500/25 px-4 py-1.5 text-xs font-black uppercase tracking-[0.25em] text-rose-200"
        >
          <span className="h-2 w-2 animate-pulse rounded-full bg-rose-400" />
          En directo
        </motion.span>

        <h2 className="text-center font-headline text-3xl font-black uppercase leading-tight tracking-tight text-white drop-shadow-[0_2px_18px_rgba(0,0,0,.9)] md:text-5xl">
          {cecop.titulo || cecop.nombredia}
        </h2>
        {fecha && <p className="text-sm capitalize text-white/60">{fecha}</p>}
        {cecop.descripcion && <p className="max-w-2xl text-center text-sm text-white/65">{cecop.descripcion}</p>}
      </div>

      <div className="relative mt-6 grid gap-4 lg:grid-cols-[1fr_300px]">
        {/* GPS de todas las cofradías del día */}
        <div className="flex flex-col gap-3">
          <div className="rounded-2xl border p-3" style={{ borderColor: `${ESCUDO_RING}44`, background: 'rgba(0,0,0,.35)' }}>
            <div className="mb-2 flex w-full flex-wrap items-center justify-center gap-x-3 gap-y-1">
              <span className="flex items-center gap-2 font-headline text-sm font-black uppercase tracking-[0.28em] text-white">
                <span style={{ color: ESCUDO_RING }}>✦</span>
                Recorridos
                <span style={{ color: ESCUDO_RING }}>✦</span>
              </span>
              <span className="flex items-center gap-1.5 text-[0.6rem] font-black uppercase tracking-[0.18em] text-rose-300">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-rose-400" />
                GPS en directo
              </span>
            </div>
            <ProcesionMap
              map={cecop.map}
              viewport={cecop.viewport}
              color={ESCUDO_RING}
              rutas={rutas}
              live={entries}
              compact
              interactive
              // Las etiquetas solo al apuntar: con un día entero de cofradías,
              // fijas taparían el mapa entero.
              hoverLabels
              onMarkerHover={(e) => { if (e?.hermandadId) setApuntada(e.hermandadId); }}
            />
            <p className="mt-1.5 text-center text-[0.6rem] text-white/45">
              Pasa el ratón por una cruz de guía o un paso para ver de quién es y su cuadro de horarios.
            </p>
          </div>

          {vid && (
            <div className="overflow-hidden rounded-2xl border border-white/12 bg-black">
              <div className="relative aspect-video">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${vid}?autoplay=1&mute=1&playsinline=1&rel=0`}
                  title={cecop.titulo}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 h-full w-full"
                />
              </div>
            </div>
          )}
        </div>

        {/* A la derecha: el cuadro de horarios de la cofradía apuntada en el
            mapa y, mientras no haya ninguna, las que salen hoy. */}
        {apuntada ? (
          <CuadroHorariosPanel
            nombre={hermandadApuntada?.nombre || filaApuntada?.nombre || 'Cofradía'}
            color={filaApuntada?.color || ESCUDO_RING}
            escudoUrl={hermandadApuntada?.escudoUrl}
            itinerario={detalleApuntado?.itinerario}
            onCerrar={() => setApuntada(null)}
          />
        ) : (
        <div className="rounded-2xl border border-white/12 bg-black/35 p-3">
          <h3 className="mb-2 text-center font-headline text-xs font-black uppercase tracking-[0.22em] text-white/80">
            Hoy salen
          </h3>
          <div className="flex max-h-[420px] flex-col gap-1.5 overflow-y-auto pr-1">
            {(cecop.hermandades || []).map((h) => {
              const hermandad = hermandades.find((x) => x.id === h.hermandadId);
              const suyas = (live?.entries || []).filter((e) => e.hermandadId === h.hermandadId);
              const enCalle = suyas.some((e) => e.online);
              return (
                <div
                  key={h.hermandadId}
                  className="flex items-center gap-2 rounded-xl border border-white/8 bg-white/[0.03] p-2"
                >
                  <span
                    className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full border-2"
                    style={{ background: '#0a0a0c', borderColor: h.color || ESCUDO_RING }}
                  >
                    {hermandad?.escudoUrl
                      ? <img src={hermandad.escudoUrl} alt="" className="h-6 w-6 object-contain" />
                      : <span className="text-[0.6rem] font-black text-white/60">✚</span>}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-bold text-white">{hermandad?.nombre || h.nombre}</span>
                    <span className="block truncate text-[0.6rem] text-white/50">
                      {h.pasos.length} paso{h.pasos.length === 1 ? '' : 's'}
                    </span>
                  </span>
                  {enCalle && (
                    <span className="shrink-0 rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[0.5rem] font-black uppercase text-emerald-300">
                      En la calle
                    </span>
                  )}
                </div>
              );
            })}
            {(cecop.hermandades || []).length === 0 && (
              <p className="py-6 text-center text-xs text-white/40">Sin cofradías configuradas.</p>
            )}
          </div>
        </div>
        )}
      </div>

      <div className="relative mt-6 flex flex-col items-stretch justify-center gap-3 sm:flex-row">
        <Link
          to="/apply"
          style={btnShape}
          className="group relative inline-flex h-12 items-center justify-center gap-2 overflow-hidden bg-gradient-to-r from-primary to-amber-500 px-7 text-sm font-bold text-primary-foreground shadow-[0_0_22px_rgba(250,204,21,0.4)] transition-transform hover:scale-[1.04]"
        >
          <DoorOpen className="h-4 w-4" /> Entrar al servidor
        </Link>

        {cecop.youtubeUrl && (
          <a
            href={cecop.youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={btnShape}
            className="group relative inline-flex h-12 items-center justify-center gap-2 overflow-hidden bg-gradient-to-r from-red-600 to-red-500 px-7 text-sm font-bold text-white shadow-[0_0_22px_rgba(239,68,68,0.35)] transition-transform hover:scale-[1.04]"
          >
            <Youtube className="h-4 w-4" /> Ver en directo
          </a>
        )}

        <a
          href="https://discord.gg/rmJGq5YbTE"
          target="_blank"
          rel="noopener noreferrer"
          style={btnShape}
          className="group relative inline-flex h-12 items-center justify-center gap-2 overflow-hidden border border-white/25 bg-white/5 px-7 text-sm font-bold text-white backdrop-blur-md transition-all hover:scale-[1.04] hover:bg-white/10"
        >
          <MessageCircle className="h-4 w-4" /> Únete al Discord
        </a>
      </div>
    </motion.div>
  );
}
